import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { WebAppUploadCompletedDataDto, WebAppUploadScheduledDataDto } from '@mi-front/api-v2';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Card, CardContent, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getUploadTitle } from './get-upload-title';
import { publishCardMessages } from '../publish-messages';
import { UploadCardContextBar } from './UploadCardContextBar';
import { UploadStatusChip } from './UploadStatusChip';
import { UploadCardDto } from './useUploadCardActions';

export interface UploadItemCardProps {
  upload: UploadCardDto;
  actionsTrigger: ReactNode;
  actionsMenuAndDialog: ReactNode;
  /**
   * When true and upload status is WAITING, the status chip shows "Esperando media"
   * (deferred fragment upload waiting for assets).
   */
  waitingForMedia?: boolean;
}

/**
 * Shared social upload card layout (fragment + composition BFF upload rows).
 * Content-first hierarchy: platform/profile + status, title, then audit trail + actions.
 */
export function UploadItemCard({
  upload,
  actionsTrigger,
  actionsMenuAndDialog,
  waitingForMedia = false,
}: UploadItemCardProps) {
  const { $t } = useIntl();
  const title = getUploadTitle(upload);

  const isCompleted = upload.status === 'COMPLETED';
  const completedData = upload.data as WebAppUploadCompletedDataDto | undefined;
  const scheduledData = upload.data as WebAppUploadScheduledDataDto | undefined;

  const dateValue = isCompleted
    ? completedData?.publishedAt
    : upload.status === 'SCHEDULED'
      ? scheduledData?.publishAt
      : upload.createdAt;

  const hasPublicUrl = Boolean(upload.platformFeatures?.publicUrl);
  /** Small text link to the public video — independent from the overflow menu. */
  const viewVideoLink = isCompleted ? (
    hasPublicUrl ? (
      <Link
        href={upload.platformFeatures!.publicUrl}
        target="_blank"
        rel="noreferrer"
        underline="hover"
        variant="body2"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
      >
        <OpenInNewIcon fontSize="inherit" />
        <FormattedMessage {...publishCardMessages.viewVideo} />
      </Link>
    ) : (
      <Tooltip title={$t(publishCardMessages.waitingPublicLink)} placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            typography: 'body2',
            color: 'text.disabled',
            flexShrink: 0,
          }}
        >
          <AccessTimeIcon fontSize="inherit" />
          <FormattedMessage {...publishCardMessages.viewVideo} />
        </Box>
      </Tooltip>
    )
  ) : null;

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent>
        <Stack spacing={1.5}>
          {/* Top bar: platform • profile | status */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <UploadCardContextBar upload={upload} />
            <UploadStatusChip upload={upload} waitingForMedia={waitingForMedia} />
          </Stack>

          {title ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
              {title}
            </Typography>
          ) : null}

          {/* Footer: audit trail (left) | primary CTA + overflow menu (bottom-right) */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography component="div" variant="body2" color="text.secondary">
              <UserLabel showIcon user={upload.creator} />
              {' • '}
              <LDateTime value={dateValue ?? upload.createdAt} showRelative="inline" />
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} flexShrink={0}>
              {viewVideoLink}
              {actionsTrigger}
              {actionsMenuAndDialog}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
